<template>
  <section  :style="{ 'background-image': `url(${imageBackground})` }" class="bg-cover px-14 sm-max:px-5 pb-14 md-max:pb-8">
    <div class="pt-6">
      <div class="grid grid-cols-12 gap-8 mt-6 sm-max:block">
        <div class="col-span-7 text-white md-max:col-span-12">
          <div class="lg:text-19 md:text-17 text-15 sm-max:!leading-5 leading-26 md-max:text-base" v-html="paragraphOne"></div>
          <br/>
          <div class="lg:text-19 md:text-17 text-15 leading-26 sm-max:!leading-5 md-max:text-base" v-html="paragraphTwo"></div>
        </div>
        <div class="flex items-center col-span-5 md-max:col-span-12 sm-max:pt-4">
          <nuxt-img
              class="m-auto mt-0 sm:w-full"
              width="504"
              height="336"
              loading="lazy"
              :alt="imageOneAlt"
              format="webp"
              sizes="sm:440px md:100vw lg:100vw xl:504px"
              :src="imageOneSrc"
          ></nuxt-img>
        </div>
      </div>
      <div class="grid grid-cols-12 gap-8 mt-10 sm-max:block md-max:mt-6" v-if="!hideBottomPart">
        <div class="flex col-span-7 text-white md-max:col-span-12 items-top">
          <div class="relative w-full">
            <nuxt-img
                :src="imageTwoSrc"
                :alt="imageTwoAlt"
                class="w-full mx-auto opacity-1 sm-max:pb-4"
                width="504"
                height="336"
                format="webp"
                sizes="sm:440px md:100vw lg:100vw xl:504px"
                loading="lazy"
            ></nuxt-img>
          </div>
        </div>
        <div class="flex col-span-5 text-white md-max:col-span-12 items-top">
          <div class="">
            <div class="lg:text-19 md:text-17 sm-max:!leading-5 text-15 leading-7 md-max:text-base lg:pr-2" v-html="paragraphThree"></div>
            <div class="lg:text-19 md:text-17 text-15 sm-max:!leading-5 leading-7 md-max:text-base mt-6 sm-max:pb-10 lg:pr-2" v-html="paragraphFour"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>

defineProps({
  hideBottomPart: {
    type: Boolean,
    default: false
  },
  paragraphOne: {
    type: String,
    default: 'High above sea level, in the verdant highlands of Nueva Segovia, lies La Esperanza Farm. Here, Oscar Flores Cruz cultivates Caturra and Catimor coffee on eight hectares (approx the size of 8 football fields) of dreams, ambition, and love. Oscar\'s journey began in 2000, not from legacy, but from humble beginnings and relentless determination.'
  },
  paragraphTwo: {
    type: String,
    default: 'Coffee is not just a job for Oscar, it\'s his lifeblood, a magical elixir that nourishes his family. He finds joy in the continuous work, the harvest, the washing, and drying of beans. His farm, La Esperanza, or \'The Hope,\' embodies his dreams and aspirations. The name signifies his unyielding faith that through coffee, he would achieve his goals.'
  },
  imageOneSrc: {
    type: String,
    default: '/assets/images/IMG_2287.jpg'
  },
  imageOneAlt: {
    type: String,
    default: 'Image of Oscar Flores Cruz\'s farm'
  },
  imageTwoSrc: {
    type: String,
    default: '/assets/images/IMG_2312.jpg'
  },
  imageTwoAlt: {
    type: String,
    default: 'Another image from the farm'
  },
  paragraphThree: {
    type: String,
    default: 'The upcoming harvest promises prosperity and the fulfillment of a long-held dream: building his house. Each coffee bean from La Esperanza is a testament to their unwavering commitment to quality. Despite the challenges of climate change and financial constraints, Oscar remains resilient and hopeful.'
  },
  paragraphFour: {
    type: String,
    default: 'To the potential buyer, Oscar offers a coffee that is not just excellent and healthy, but a cup filled with a story of resilience, passion, and hope. From the heights of Nueva Segovia to your cup, Oscar invites you to share in the journey of La Esperanza.'
  },
  imageBackground: {
    type: String,
    default: '/assets/images/freebag-sect10-bg.jpg'
  }
})

defineBuilderComponent({
    friendlyName: "ResilienceHopeCup",
    canHaveChildren: true,
    inputs: [
        {
            name: "hideBottomPart",
            type: "boolean",
            defaultValue: false
        },
        {
            name: "imageBackground",
            type: "file",
            allowedFileTypes: [
                "jpeg",
                "jpg",
                "svg",
                "png"
            ],
            required: true,
            defaultValue: "/assets/images/freebag-sect10-bg.jpg"
        },
        {
            name: "paragraphOne",
            type: "richText",
            defaultValue: "High above sea level, in the verdant highlands of Nueva Segovia, lies La Esperanza Farm. Here, Oscar Flores Cruz cultivates Caturra and Catimor coffee on eight hectares (approx the size of 8 football fields) of dreams, ambition, and love. Oscar's journey began in 2000, not from legacy, but from humble beginnings and relentless determination."
        },
        {
            name: "paragraphTwo",
            type: "richText",
            defaultValue: "Coffee is not just a job for Oscar, it's his lifeblood, a magical elixir that nourishes his family. He finds joy in the continuous work, the harvest, the washing, and drying of beans. His farm, La Esperanza, or 'The Hope,' embodies his dreams and aspirations. The name signifies his unyielding faith that through coffee, he would achieve his goals."
        },
        {
            name: "imageOneSrc",
            type: "file",
            allowedFileTypes: [
                "jpeg",
                "jpg",
                "svg",
                "png"
            ],
            required: true,
            defaultValue: "/assets/images/IMG_2287.jpg"
        },
        {
            name: "imageOneAlt",
            type: "string",
            defaultValue: "Image of Oscar Flores Cruz's farm"
        },
        {
            name: "imageTwoSrc",
            type: "file",
            allowedFileTypes: [
                "jpeg",
                "jpg",
                "svg",
                "png"
            ],
            required: true,
            defaultValue: "/assets/images/IMG_2312.jpg"
        },
        {
            name: "imageTwoAlt",
            type: "string",
            defaultValue: "Another image from the farm"
        },
        {
            name: "paragraphThree",
            type: "richText",
            defaultValue: "The upcoming harvest promises prosperity and the fulfillment of a long-held dream: building his house. Each coffee bean from La Esperanza is a testament to their unwavering commitment to quality. Despite the challenges of climate change and financial constraints, Oscar remains resilient and hopeful."
        },
        {
            name: "paragraphFour",
            type: "richText",
            defaultValue: "To the potential buyer, Oscar offers a coffee that is not just excellent and healthy, but a cup filled with a story of resilience, passion, and hope. From the heights of Nueva Segovia to your cup, Oscar invites you to share in the journey of La Esperanza."
        }
    ]
});
</script>